import clsx from "clsx";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import generatePDF, { Margin, type Options } from "react-to-pdf";

import { Gallery } from "@features/article-content/components";
import type { GalleryBlockType } from "@features/article-content/model";
import type { TicketType } from "@features/ticket-card/model";

import { regexTags } from "@shared/constants";
import { BrandIcon } from "@shared/ui/brand-icon";
import { Button } from "@shared/ui/button";
import { ButtonWithIcon } from "@shared/ui/button-with-icon";
import { Icon } from "@shared/ui/icon";
import { MessageWrapper } from "@shared/ui/message-wrapper";
import { SharedSocial } from "@shared/ui/social-shared";
import { Body, Heading } from "@shared/ui/typography";

import type { Chat } from "../../model";
import { useShareLink } from "../../model/useShareLink";
import { detachHidePdfElements, getEndTime } from "../../model/utils";
import { Card } from "./card";
import type { CardKey } from "./chat-content";

import s from "./chat-content.module.scss";

const cardsTitle = {
  tickets: "Билеты и ски-пассы",
  equipments: "Доступные точки проката и аренды снаряжения",
  cafes: "",
  accommodations: "",
} as const;

type AnswerProps = {
  item: Chat;
  onRefresh?: (text: string) => void;
  onRefreshProgram?: (data: Chat["question"]) => void;
  onCardClick: (key: CardKey, data: TicketType) => void;
};

export const FALLBACK_TYPES: Record<string, string> = {
  excursion: "cultural-objects",
  ecotraps: "ecotrails",
};

export const Answer = (props: AnswerProps) => {
  const { item, onRefresh, onCardClick, onRefreshProgram } = props;

  const targetRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  const handleCopyText = useCallback(async (data: string) => {
    const text = data.replace(regexTags, "");

    try {
      await navigator?.clipboard?.writeText(text);
    } catch {
      console.error("ошибка копирования текста");
    }
  }, []);

  const handleRefresh = useCallback(() => {
    if (onRefresh && item?.question) {
      onRefresh(item.question as string);
    }
  }, [onRefresh, item?.question]);

  const handleRefreshProgramm = useCallback(() => {
    if (onRefreshProgram && item?.question) {
      onRefreshProgram(item?.question);
    }
  }, [onRefreshProgram, item?.question]);

  const isProgram = useMemo(() => !!item.schedule, [item.schedule]);

  useEffect(() => {
    const root = contentRef.current;
    if (!root) return;

    const links = root.querySelectorAll<HTMLAnchorElement>("a");
    links.forEach((link) => {
      link.target = "_blank";
      if (!link.rel) {
        link.rel = "noopener noreferrer";
      }
    });
  }, [item.text]);

  const handleCreatePDF = useCallback(() => {
    const root = targetRef.current;
    if (!root) return;

    const options: Options = {
      method: "save",
      filename: "mamison-program.pdf",
      page: {
        margin: Margin.SMALL,
        format: "A4",
        orientation: "portrait",
      },
      canvas: {
        mimeType: "image/jpeg",
        qualityRatio: 1,
      },
      overrides: {
        pdf: {
          compress: true,
        },
        canvas: {
          useCORS: true,
          allowTaint: false,
        },
      },
    };

    const fallbackOptions: Options = {
      ...options,
      overrides: {
        ...options.overrides,
        canvas: {
          ...options.overrides?.canvas,
          useCORS: true,
          allowTaint: false,
          ignoreElements: (element) =>
            ["IMG", "SVG", "VIDEO", "CANVAS"].includes(element.tagName),
        },
      },
    };

    const restore = detachHidePdfElements(root);
    generatePDF(targetRef, options)
      .catch((error) => {
        const isTaintedCanvasError =
          error instanceof DOMException && error.name === "SecurityError";

        if (!isTaintedCanvasError) {
          throw error;
        }

        console.warn(
          "[AI Assistant] PDF fallback: generate without external media because of CORS restrictions",
        );

        return generatePDF(targetRef, fallbackOptions);
      })
      .catch((error) => {
        console.error("[AI Assistant] PDF generation failed", error);
      })
      .finally(() => {
        restore();
      });
  }, []);

  const [isMore, setIsMore] = useState(false);

  const currentAccomandation = useMemo(() => {
    if (!Array.isArray(item?.accommodation)) return [];

    return isMore ? item.accommodation : [...item.accommodation].slice(0, 1);
  }, [isMore, item?.accommodation]);

  const viewMoreBtn = currentAccomandation && currentAccomandation.length > 0;

  if (item.type === "question") {
    return (
      <MessageWrapper variant="right-bottom" className={s.question}>
        {item.text}
      </MessageWrapper>
    );
  }

  return (
    <div className={s.answerWrapper} ref={targetRef} id={item.key}>
      <BrandIcon
        name="three-circle"
        fillColor="violet"
        strokeColor="milk"
        className={s.anserIcon}
      />

      {isProgram && (
        <div className={clsx(s.shareProgram, s.topShare, "hide-pdf")}>
          <div className={s.buttons}>
            <div className={s.shareButtonWrapper}>
              <div className={s.shareButton}>
                <Body size="uppercase-small" weight="semiBold">
                  Поделиться
                </Body>
                <div className={s.icon}>
                  <Icon name="share" />
                </div>
              </div>
              <div className={s.share}>
                <AISharedSocial answer={item} isProgram />
              </div>
            </div>
            <Button onClick={handleCreatePDF} className={s.button}>
              <div className={s.shareButton}>
                <Body size="uppercase-small" weight="semiBold">
                  Скачать программу
                </Body>
                <div className={s.icon}>
                  <Icon name="download" />
                </div>
              </div>
            </Button>
          </div>
          <Button className={s.icon} onClick={handleRefreshProgramm}>
            <Icon name="refresh" />
          </Button>
        </div>
      )}

      <div
        ref={contentRef}
        dangerouslySetInnerHTML={{ __html: item.text }}
        className={s.answer}
      />
      {item?.data && item?.data?.length > 0 && (
        <div className={s.actualData}>
          <Heading level={6}>
            {cardsTitle[item.key as keyof typeof cardsTitle]}
          </Heading>
          <div className={s.cards}>
            {item?.data?.map((card) => (
              <Card
                key={card.documentId}
                type={item.key as never}
                cardData={card}
                clickable
                onCardClick={() =>
                  onCardClick(item.key as CardKey, card as TicketType)
                }
              />
            ))}
          </div>
        </div>
      )}

      {item?.accommodation && (
        <Heading level={6} className={clsx(s.subtitle, "hide-pdf")}>
          Выбрали для вас лучшее жилье
        </Heading>
      )}

      {currentAccomandation.length > 0 ? (
        currentAccomandation.map((card) => (
          <Card
            key={card.key}
            type={"accommodations-program"}
            cardData={card as never}
            className={clsx(s.accommodationCard, "hide-pdf")}
            clickable
            onCardClick={() =>
              onCardClick(
                `/accommodations/${card?.slug}` as CardKey,
                card?.accommodation as never,
              )
            }
          />
        ))
      ) : (
        <Card
          key={item?.accommodation?.slug}
          type={"accommodations-program"}
          cardData={item?.accommodation as never}
          className={clsx(s.accommodationCard, "hide-pdf")}
          clickable
          onCardClick={() =>
            onCardClick(
              `/accommodations/${item?.accommodation?.slug}` as CardKey,
              item?.accommodation as never,
            )
          }
        />
      )}

      {viewMoreBtn && (
        <div className={clsx(s.moreBtn, "hide-pdf")}>
          <ButtonWithIcon
            iconFillColor="azure"
            onClick={() => {
              setIsMore((prevState) => !prevState);
            }}
            variant="primary-dark"
            iconName="circle"
          >
            {isMore ? "Скрыть" : "Показать еще"}
          </ButtonWithIcon>
        </div>
      )}

      {item?.schedule && (
        <Heading level={6} className={clsx(s.subtitle, "hide-pdf")}>
          Составили для вас расписание
        </Heading>
      )}

      {item?.schedule &&
        item?.schedule?.length > 0 &&
        item?.schedule.map((day) => {
          const images: GalleryBlockType["images"] = day.items
            .filter((img) => Boolean(img.image_url))
            .map((img) => ({
              url: img.image_url,
              alternativeText: img.description,
              caption: img.description,
            })) as GalleryBlockType["images"];

          return (
            <div key={day.day} className={s.dayWrapper}>
              <Heading
                level={6}
                tag="p"
                className={s.dayCount}
              >{`${day.day} день`}</Heading>
              <div className={s.dayList}>
                {day.items.map((val) => {
                  return (
                    <Button
                      href={`/${FALLBACK_TYPES?.[val.type] || val.type}/${val.slug}`}
                      target="_blank"
                      key={val.title}
                      className={s.dayItem}
                    >
                      <Body weight="semiBold" className={s.itemTime}>
                        {getEndTime(val.time, val.duration)}
                      </Body>
                      <Body className={s.itemTitle}>{val.description}</Body>
                      <Icon name="arrow-right" className={s.arrowIcon} />
                    </Button>
                  );
                })}
              </div>
              {images.length > 0 && (
                <div className={clsx(s.gallery, "hide-pdf")}>
                  <Gallery
                    id={day.day}
                    __component="article.gallery"
                    images={images}
                  />
                </div>
              )}
            </div>
          );
        })}

      {item.query_response && (
        <div
          dangerouslySetInnerHTML={{ __html: item.query_response }}
          className={s.answer}
        />
      )}

      {!isProgram && (
        <div className={s.bottomIcons}>
          <Button className={s.icon} onClick={handleRefresh}>
            <Icon name="refresh" />
          </Button>
          <Button className={s.icon} onClick={() => handleCopyText(item.text)}>
            <Icon name="copy" />
          </Button>
          <div className={s.shareIcon}>
            <div className={s.icon}>
              <Icon name="share" />
            </div>
            <div className={s.share}>
              <AISharedSocial answer={item} />
            </div>
          </div>
        </div>
      )}

      {isProgram && (
        <div className={clsx(s.shareProgram, "hide-pdf")}>
          <div className={s.buttons}>
            <div className={s.shareButtonWrapper}>
              <div className={s.shareButton}>
                <Body size="uppercase-small" weight="semiBold">
                  Поделиться
                </Body>
                <div className={s.icon}>
                  <Icon name="share" />
                </div>
              </div>
              <div className={s.share}>
                <AISharedSocial answer={item} isProgram />
              </div>
            </div>
            <Button onClick={handleCreatePDF} className={s.button}>
              <div className={s.shareButton}>
                <Body size="uppercase-small" weight="semiBold">
                  Скачать программу
                </Body>
                <div className={s.icon}>
                  <Icon name="download" />
                </div>
              </div>
            </Button>
          </div>
          <Button className={s.icon} onClick={handleRefreshProgramm}>
            <Icon name="refresh" />
          </Button>
        </div>
      )}
    </div>
  );
};

const AISharedSocial = ({
  answer,
  isProgram,
}: {
  answer: Chat;
  isProgram?: boolean;
}) => {
  const share = useShareLink(answer, isProgram);
  return <SharedSocial isSmall customHandlerCopy={share} />;
};

Answer.displayName = "Answer";
